/**
 * 通用格式化工具函数
 */
export const klFormat = {
	/**
	 * 格式化金额
	 * @param amount 金额
	 * @param currency 货币符号，默认为 '¥'
	 * @param decimals 小数位数，默认为 2
	 * @returns 格式化后的金额字符串或 '--'
	 */
	formatCurrency(
		amount: number | string | null | undefined,
		currency: string = '¥',
		decimals: number = 3,
	): string {
		if (amount === null || amount === undefined || isNaN(Number(amount))) {
			return '--'
		}
		const num = Number(amount)
		return (
			currency +
			num.toLocaleString('zh-CN', {
				minimumFractionDigits: decimals,
				maximumFractionDigits: decimals,
			})
		)
	},
	/**
	* TODO 修改国家的货币符号
	 * 根据国家ID获取货币符号
	 * @param cnty 国家ID，默认为8（菲律宾）
	 * @returns 货币符号
	 */
	formatCurrencySymbol(cnty: number = 8): string {
		// 国家ID到货币符号的映射（基于后端 KLCountry 和 KLCurrency）
		const currencySymbolMap: Record<number, string> = {
			1: '¥',    // 中国 - CNY
			8: '₱',    // 菲律宾 - PHP
			15: '$',   // 美国 - USD
		}
		return currencySymbolMap[cnty] || '₱' // 默认返回菲律宾比索符号
	},

	/**
	 * 格式化数字，添加千分位
	 * @param num 数字
	 * @param decimals 小数位数，默认为 0
	 * @returns 格式化后的数字字符串或 '--'
	 */
	formatNumber(num: number | string | null | undefined, decimals: number = 0): string {
		if (num === null || num === undefined || isNaN(Number(num))) {
			return '--'
		}
		const number = Number(num)
		return number.toLocaleString('zh-CN', {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		})
	},

	/**
	 * 格式化百分比
	 * @param value 数值 (例如 0.5 或 50)
	 * @param isDecimal 是否为小数形式 (0-1)，默认为 true
	 * @param decimals 小数位数，默认为 2
	 * @returns 格式化后的百分比字符串或 '--'
	 */
	formatPercent(
		value: number | string | null | undefined,
		isDecimal: boolean = true,
		decimals: number = 2,
	): string {
		if (value === null || value === undefined || isNaN(Number(value))) {
			return '--'
		}
		let num = Number(value)
		if (isDecimal) {
			num *= 100
		}
		return `${num.toFixed(decimals)}%`
	},

	/**
	 * 格式化文件大小
	 * @param bytes 字节数
	 * @param decimals 小数位数，默认为 2
	 * @returns 格式化后的文件大小字符串或 '--'
	 */
	formatFileSize(bytes: number | null | undefined, decimals: number = 2): string {
		if (bytes === null || bytes === undefined || bytes < 0) {
			return '--'
		}
		if (bytes === 0) return '0 Bytes'

		const k = 1024
		const dm = decimals < 0 ? 0 : decimals
		const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

		const i = Math.floor(Math.log(bytes) / Math.log(k))

		return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
	},

	/**
	 * 遮挡敏感信息
	 * @param str 原始字符串
	 * @param start 保留前几位
	 * @param end 保留后几位
	 * @param mask 遮挡字符，默认为 '*'
	 * @returns 遮挡后的字符串
	 */
	maskSensitiveInfo(
		str: string | null | undefined,
		start: number,
		end: number,
		mask: string = '*',
	): string {
		if (!str) {
			return '--'
		}
		if (str.length <= start + end) {
			return str // 字符串太短，无需遮挡
		}
		const maskedLength = str.length - start - end
		const maskedPart = mask.repeat(maskedLength)
		return str.substring(0, start) + maskedPart + str.substring(str.length - end)
	},

	/**
	 * 格式化手机号，可选择遮挡中间四位
	 * @param phone 手机号
	 * @param doMask 是否遮挡，默认为 true
	 * @returns 格式化后的手机号或 '--'
	 */
	formatPhone(phone: string | null | undefined, doMask: boolean = true): string {
		if (!phone) {
			return '--'
		}
		const cleaned = phone.replace(/\D/g, '') // 移除所有非数字字符
		if (cleaned.length !== 11) {
			return phone // 不是11位手机号，不处理
		}
		if (doMask) {
			return cleaned.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
		}
		return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
	},

	/**
	 * 格式化状态，返回包含标签和颜色的对象
	 * @param status 状态值
	 * @param statusMap 状态映射对象，例如 { 'ACTIVE': { label: '活跃', color: 'green' } }
	 * @param defaultLabel 默认标签
	 * @param defaultColor 默认颜色
	 * @returns 包含 label 和 color 的对象
	 */
	formatStatus(
		status: string | number | null | undefined,
		statusMap: { [key: string]: { label: string; color: string } },
		defaultLabel: string = '未知',
		defaultColor: string = 'default',
	): { label: string; color: string } {
		if (status === null || status === undefined) {
			return { label: defaultLabel, color: defaultColor }
		}
		const key = String(status)
		return statusMap[key] || { label: defaultLabel, color: defaultColor }
	},
}



